import { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { ChartFragment } from 'models/api-response';
import { UserRole, ValidationActionType } from 'models/enum';
import { ChartComponentProps, ChartMetaFormField } from 'models/ui';
import { Locales } from 'constants/app.constant';
import { assignmentHelper, chartHelper } from 'helpers';
import { chartService } from 'services';
import { appSelectors } from 'redux/ducks/app';
import { studentSelectors } from 'redux/ducks/student';
import { withChartLogic } from 'components/common';
import { Title } from 'components/features/chart/special-charts/shared/constants';
import withSavedPatientChartsPage from 'components/features/shared/withSavedPatientChartsPage';
import {
  AdvanceDirectiveFieldConfig,
  FIELD_CONFIGS,
  FIELD_CONFIG_MAP,
  FormField,
  hasFieldValue,
  PROVIDER_NURSE_FIELD,
  PROVIDER_NURSE_LABEL,
  SAVED_STATEMENTS,
  SAVED_TITLE_MAP,
  STATIC_TEXT,
  STEP_CONFIGS,
  STEP_REQUIRED_EXTRAS,
  SectionTitle
} from './constants';
import AdvanceDirectiveView, { AdvanceDirectiveViewProps } from './AdvanceDirectiveView';

export interface AdvanceDirectiveProps extends ChartComponentProps {
  authorName?: string;
  enableMultiStepsAuthoring?: (isSupportMultiStepsAuthoring: boolean) => void;
  locale?: Locales;
  navigateToSavedPatientCharting: VoidFunction;
  userRole?: string;
}

const LAST_STEP_INDEX = STEP_CONFIGS.length - 1;

const buildFormField = (fieldConfig: AdvanceDirectiveFieldConfig): ChartMetaFormField => {
  const formField = chartHelper.createFormField({ name: fieldConfig.name, type: fieldConfig.type, label: fieldConfig.label });

  return {
    ...formField,
    isRequired: Boolean(fieldConfig.isRequired),
    value: ''
  };
};

export const buildDefaultFormFields = (): Map<string, ChartMetaFormField> => {
  const dataMap = new Map();

  FIELD_CONFIGS.forEach((fieldConfig) => {
    dataMap.set(fieldConfig.name, buildFormField(fieldConfig));
  });

  return dataMap;
};

const buildRequiredSanitizedFieldMap = (formFieldMap: Map<string, ChartMetaFormField>): Map<string, ChartMetaFormField> => {
  const sanitizedMap = new Map<string, ChartMetaFormField>();

  formFieldMap.forEach((field, name) => {
    sanitizedMap.set(name, {
      ...field,
      isRequired: false,
      errors: (field.errors || []).filter((error) => error?.type !== ValidationActionType.REQUIRED)
    });
  });

  return sanitizedMap;
};

const buildProviderNurseRecord = (isAuthor: boolean, authorName: string, student: { firstName?: string; lastName?: string } | undefined) => {
  const resolvedName = isAuthor ? (authorName || '').trim() : `${student?.firstName ?? ''} ${student?.lastName ?? ''}`.trim();
  if (!resolvedName) return null;
  return chartService.buildPlainRecord({
    formField: PROVIDER_NURSE_FIELD,
    title: PROVIDER_NURSE_LABEL,
    content: resolvedName,
    value: resolvedName,
    contentId: ''
  });
};

const AdvanceDirective = (props: AdvanceDirectiveProps) => {
  const {
    authorName,
    chartingTime,
    displayAuthoringData,
    enableDisplayRecordsButton,
    enableMultiStepsAuthoring,
    formFieldMap,
    formSubmittedCount,
    initState,
    locale,
    navigateToSavedPatientCharting,
    resetFormSubmitted,
    saveChart,
    student,
    userRole
  } = props;
  const [activeStep, setActiveStep] = useState(0);
  const [furthestStep, setFurthestStep] = useState(0);
  const safeFormFieldMap = formFieldMap || buildDefaultFormFields();
  const safeFormFieldMapWithoutRequiredErrors = useMemo(() => buildRequiredSanitizedFieldMap(safeFormFieldMap), [safeFormFieldMap]);

  // Demo and Add Data to EHR instructors can chart and save, like Discharge Planning. Other instructors
  // can fill in and step through the chart to demonstrate it, but the Save action is withheld from them.
  const isInstructorEditMode = assignmentHelper.isDemoEmptyEhr() || assignmentHelper.isInstructorAddDataToEhr(locale as Locales);
  const isInstructorDemoView = userRole === UserRole.INSTRUCTOR && !props.isAuthor && !isInstructorEditMode;

  useEffect(() => {
    // Always start from a blank chart; previously saved data is never reloaded into the form.
    initState(buildDefaultFormFields());
    enableMultiStepsAuthoring?.(true);
  }, [enableMultiStepsAuthoring, initState]);

  const isStepComplete = useCallback(
    (stepIndex: number) =>
      [...STEP_CONFIGS[stepIndex].fields, ...STEP_REQUIRED_EXTRAS[stepIndex]].every((fieldName) => {
        if (!FIELD_CONFIG_MAP.get(fieldName)?.isRequired) return true;
        return hasFieldValue(safeFormFieldMap.get(fieldName));
      }),
    [safeFormFieldMap]
  );

  const isFinalStepReady = STEP_CONFIGS.every((_, stepIndex) => isStepComplete(stepIndex));
  // A step is visually completed only after user progresses past it via Continue.
  const completedSteps = STEP_CONFIGS.map((_, index) => index < furthestStep);

  const buildFragment = useCallback((): ChartFragment => {
    const buildTextRecord = (fieldName: FormField) => {
      const formField = safeFormFieldMap.get(fieldName);
      if (!formField || !hasFieldValue(formField)) return null;

      return chartService.buildPlainRecord({
        formField: formField.name,
        title: SAVED_TITLE_MAP[fieldName] || FIELD_CONFIG_MAP.get(fieldName)?.label || '',
        content: formField.value,
        value: formField.value
      });
    };

    // Declaration statements are static legal text, so they always appear with no entered value.
    const buildStatementRecord = (formField: string, title: string) => chartService.buildPlainRecord({ formField, title, content: '' });

    const generalInfoRecords = [buildTextRecord(FormField.DECLARANT_NAME)];
    const providerNurseRecord = buildProviderNurseRecord(props.isAuthor, authorName, student);
    if (providerNurseRecord) {
      generalInfoRecords.unshift(providerNurseRecord);
    }

    const record = {
      chartTitle: Title.SPECIAL_CHARTS,
      fragmentTitle: SectionTitle.ADVANCE_DIRECTIVE,
      records: [
        {
          sectionTitle: SectionTitle.GENERAL_INFO,
          useSectionTitleAsHeader: true,
          records: generalInfoRecords
        },
        {
          sectionTitle: STATIC_TEXT.PHYSICAL_HEALTH_CARE_TITLE,
          useSectionTitleAsHeader: true,
          records: [buildStatementRecord('physicalHealthCareIntro', SAVED_STATEMENTS.PHYSICAL_HEALTH_CARE)]
        },
        {
          sectionTitle: SectionTitle.HEALTH_CARE_PROXY,
          useSectionTitleAsHeader: true,
          records: [
            buildStatementRecord('agentAppointment', SAVED_STATEMENTS.AGENT_APPOINTMENT),
            buildTextRecord(FormField.AGENT_NAME),
            buildTextRecord(FormField.AGENT_ADDRESS),
            buildTextRecord(FormField.AGENT_PHONE_NUMBER),
            buildStatementRecord('altAgentAppointment', SAVED_STATEMENTS.ALT_AGENT_APPOINTMENT),
            buildTextRecord(FormField.ALT_AGENT_NAME),
            buildTextRecord(FormField.ALT_AGENT_ADDRESS),
            buildTextRecord(FormField.ALT_AGENT_PHONE_NUMBER)
          ]
        },
        {
          sectionTitle: SectionTitle.STATEMENT_OF_DESIRES,
          useSectionTitleAsHeader: true,
          records: [buildStatementRecord('generalIntent', SAVED_STATEMENTS.GENERAL_INTENT), buildStatementRecord('lifeSustainingTreatment', SAVED_STATEMENTS.LIFE_SUSTAINING)]
        },
        {
          sectionTitle: SectionTitle.DECLARATION_OF_UNDERSTANDING,
          useSectionTitleAsHeader: true,
          records: [
            buildStatementRecord('declarationUnderstanding', SAVED_STATEMENTS.DECLARATION_OF_UNDERSTANDING),
            buildTextRecord(FormField.DECLARATION_NAME_PRINT),
            buildTextRecord(FormField.DECLARATION_SIGNATURE)
          ]
        },
        {
          sectionTitle: SectionTitle.WITNESSES,
          useSectionTitleAsHeader: true,
          records: [
            buildStatementRecord('witnessesStatement', SAVED_STATEMENTS.WITNESSES),
            buildTextRecord(FormField.WITNESS_1_NAME),
            buildTextRecord(FormField.WITNESS_1_ADDRESS),
            buildTextRecord(FormField.WITNESS_1_SIGNATURE),
            buildTextRecord(FormField.WITNESS_2_NAME),
            buildTextRecord(FormField.WITNESS_2_ADDRESS),
            buildTextRecord(FormField.WITNESS_2_SIGNATURE),
            buildTextRecord(FormField.WITNESS_3_NAME),
            buildTextRecord(FormField.WITNESS_3_ADDRESS),
            buildTextRecord(FormField.WITNESS_3_SIGNATURE)
          ]
        }
      ]
    };

    const cleanRecord = chartService.systemAssessment.removeEmptyRecords(record);
    const basicInfo = chartService.createBaseFragment({ chartingTime });
    return { ...basicInfo, chartData: cleanRecord } as ChartFragment;
  }, [authorName, chartingTime, props.isAuthor, safeFormFieldMap, student]);

  const handleSaveClick = useCallback(() => {
    saveChart([buildFragment()], {
      formFieldMap: safeFormFieldMapWithoutRequiredErrors,
      defaultFormFields: buildDefaultFormFields(),
      afterSave: () => {
        // Students are taken to the Saved Special Chart page once the chart is complete.
        // Authors stay in place; the save enables the "Return with data" action so they can review the saved records.
        if (!props.isAuthor) {
          navigateToSavedPatientCharting();
        }
      }
    });
  }, [buildFragment, navigateToSavedPatientCharting, props.isAuthor, safeFormFieldMapWithoutRequiredErrors, saveChart]);

  const handleSelectStep = useCallback(
    (stepIndex: number) => {
      if (stepIndex > furthestStep) return;
      setActiveStep(stepIndex);
      resetFormSubmitted?.();
    },
    [furthestStep, resetFormSubmitted]
  );

  const handleBackStep = useCallback(() => {
    if (activeStep === 0) return;
    setActiveStep(activeStep - 1);
    resetFormSubmitted?.();
  }, [activeStep, resetFormSubmitted]);

  const handleContinueStep = useCallback(() => {
    if (activeStep === LAST_STEP_INDEX) {
      if (!isFinalStepReady || isInstructorDemoView) return;
      handleSaveClick();
      return;
    }

    const nextStep = activeStep + 1;
    setFurthestStep((previousStep) => Math.max(previousStep, nextStep));
    setActiveStep(nextStep);
    resetFormSubmitted?.();
  }, [activeStep, handleSaveClick, isFinalStepReady, isInstructorDemoView, resetFormSubmitted]);

  const handleResetStep = useCallback(() => {
    [...STEP_CONFIGS[activeStep].fields, ...STEP_REQUIRED_EXTRAS[activeStep]].forEach((fieldName) => {
      const formField = safeFormFieldMap.get(fieldName);
      if (formField && String(formField.value ?? '') !== '') {
        formField.onChange?.(formField, { target: { value: '' } });
      }
    });
  }, [activeStep, safeFormFieldMap]);

  const canReset = [...STEP_CONFIGS[activeStep].fields, ...STEP_REQUIRED_EXTRAS[activeStep]].some((fieldName) => hasFieldValue(safeFormFieldMap.get(fieldName)));

  const viewProps: AdvanceDirectiveViewProps = {
    activeStep,
    canContinue: activeStep === LAST_STEP_INDEX ? isFinalStepReady : isStepComplete(activeStep),
    canReset,
    completedSteps,
    displayAuthoringData,
    enableDisplayRecordsButton: Boolean(enableDisplayRecordsButton),
    formFieldMap: safeFormFieldMapWithoutRequiredErrors,
    formSubmittedCount,
    furthestStep,
    isAuthor: Boolean(props.isAuthor),
    isDemoView: isInstructorDemoView,
    isReadOnly: false,
    onBackStep: handleBackStep,
    onContinueStep: handleContinueStep,
    onResetStep: handleResetStep,
    onSelectStep: handleSelectStep
  };

  return <AdvanceDirectiveView {...viewProps} />;
};

export { AdvanceDirective as BaseAdvanceDirective };

const mapStateToProps = (state) => ({
  authorName: studentSelectors.getAuthorName(state),
  locale: appSelectors.getLocale(state),
  userRole: appSelectors.getUserRole(state)
});

export default compose(connect(mapStateToProps), withSavedPatientChartsPage, withChartLogic)(AdvanceDirective);
